import { motion, useMotionValue, useSpring } from "framer-motion";
import { useEffect, useState } from "react";
import { FiMonitor, FiLayers, FiZap, FiTrendingUp } from "react-icons/fi";

export default function Hero() {
  const [isLoaded, setIsLoaded] = useState(false);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const springX = useSpring(mouseX, { stiffness: 80, damping: 25 });
  const springY = useSpring(mouseY, { stiffness: 80, damping: 25 });

  useEffect(() => {
  setIsLoaded(true);

  const move = (e) => {
    mouseX.set(e.clientX);
    mouseY.set(e.clientY);
  };

  window.addEventListener("mousemove", move);
  return () => window.removeEventListener("mousemove", move);
}, []);
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({
        top: el.offsetTop - 90,
        behavior: "smooth",
      });
    }
  };

  const services = [
    { icon: <FiMonitor />, label: "Web Dev" },
    { icon: <FiLayers />, label: "Branding" },
    { icon: <FiZap />, label: "Automation" },
    { icon: <FiTrendingUp />, label: "Growth" },
  ];

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center bg-gradient-to-br from-[#f8fafc] via-[#ecfeff] to-[#ffffff] overflow-hidden pt-20"
    >
      {/* subtle grid texture */}
      <div className="absolute inset-0 opacity-[0.04] bg-[linear-gradient(#000_1px,transparent_1px),linear-gradient(90deg,#000_1px,transparent_1px)] bg-[size:60px_60px]" />

      {/* background blobs */}
      <div className="absolute inset-0">
        <motion.div
          animate={{ scale: [1, 1.1, 1], rotate: [0, 90, 0] }}
          transition={{ duration: 18, repeat: Infinity }}
          className="absolute top-[-15%] left-[-10%] w-[50vw] h-[50vw] bg-teal-200/30 blur-[120px] rounded-full"
        />

        <motion.div
          animate={{ scale: [1.1, 1, 1.1], rotate: [90, 0, 90] }}
          transition={{ duration: 20, repeat: Infinity }}
          className="absolute bottom-[-15%] right-[-10%] w-[45vw] h-[45vw] bg-emerald-200/30 blur-[120px] rounded-full"
        />

        <motion.div
          style={{ x: springX, y: springY }}
          className="absolute w-80 h-80 bg-cyan-300/10 rounded-full blur-[80px]"
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-10 items-center relative z-10">

        {/* LEFT */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={isLoaded ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          {/* badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-teal-100 shadow-sm mb-5">
            <span className="h-2 w-2 bg-teal-500 rounded-full animate-pulse" />
            <span className="text-[10px] tracking-[0.25em] font-bold text-teal-700 uppercase">
              Aysu Groups • Digital Studio
            </span>
          </div>

          {/* title */}
          <h1 className="text-4xl md:text-6xl font-black leading-[1.05] mb-5">
            Smart Digital <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 via-emerald-500 to-cyan-600">
              Solutions That Scale
            </span>
          </h1>

          {/* description */}
          <p className="text-base md:text-lg text-slate-500 max-w-xl mb-6 leading-relaxed">
            We build modern websites, branding systems, automation tools, and
            growth-focused digital products for businesses.
          </p>

          {/* CTA */}
          <div className="flex items-center gap-5 mb-8">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => scrollTo("contact")}
              className="relative px-8 py-3 rounded-2xl bg-black text-white font-semibold shadow-lg overflow-hidden"
            >
              <span className="relative z-10">Start Project</span>
              <span className="absolute inset-0 bg-gradient-to-r from-teal-600 via-emerald-500 to-cyan-600 opacity-0 hover:opacity-100 transition" />
            </motion.button>

            <button
              onClick={() => scrollTo("projects")}
              className="text-sm font-semibold text-slate-700 hover:text-teal-600 transition"
            >
              View Work →
            </button>
          </div>

          {/* stats */}
          <div className="grid grid-cols-3 gap-4 max-w-md">
            {[
              { label: "Projects", value: "20+" },
              { label: "Clients", value: "15+" },
              { label: "Growth", value: "100%" },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white/60 backdrop-blur-xl border border-white rounded-xl p-3 text-center shadow-sm"
              >
                <h3 className="text-xl font-black text-teal-600">
                  {item.value}
                </h3>
                <p className="text-[11px] text-slate-500">{item.label}</p>
              </div>
            ))}
          </div>

          {/* trust strip */}
          <div className="mt-6 text-xs text-slate-400">
            Trusted for fast, scalable digital solutions.
          </div>
        </motion.div>

        {/* RIGHT */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={isLoaded ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8 }}
          className="hidden lg:flex justify-center relative"
        >
          {/* main card */}
          <div className="relative w-[340px] h-[420px] rounded-[3rem] bg-white/40 backdrop-blur-3xl border border-white shadow-2xl flex items-center justify-center">
            <img
              src="/Logo.jpg"
              alt="Aysu Groups"
              className="w-full h-full object-contain p-10"
            />
          </div>

          {/* floating services */}
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 5, repeat: Infinity }}
            className="absolute -bottom-14 bg-white/80 backdrop-blur-xl border border-white shadow-xl rounded-3xl p-4 w-64"
          >
            <h4 className="text-xs font-bold mb-3 text-slate-700">
              Core Services
            </h4>

            <div className="grid grid-cols-2 gap-2">
              {services.map((s, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 p-2 rounded-xl hover:bg-teal-50 transition"
                >
                  <span className="text-teal-600 text-sm">{s.icon}</span>
                  <span className="text-[11px] font-medium">{s.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* scroll indicator */}
      <motion.div
        animate={{ y: [0, 6, 0], opacity: [0.4, 1, 0.4] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs text-teal-600"
      >
        Scroll ↓
      </motion.div>
    </section>
  );
}